import {Box, Center} from "@chakra-ui/react";
import {Link, useLocation} from "react-router-dom";
import { Helmet } from 'react-helmet';
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { supabase } from '../utils/supabaseClient.js';
import {
  captureRecoveryFromUrl,
  clearPasswordRecovery,
  isPasswordRecoveryActive,
  markPasswordRecovery,
} from '../utils/passwordRecovery';
import {useState, useEffect, useMemo} from 'react';

const AUTH_UI_VIEWS = [
  'sign_in',
  'sign_up',
  'magic_link',
  'forgotten_password',
  'update_password',
  'verify_otp',
];

/** Supabase Auth UI: merges with English defaults; override all strings you care about */
const authUiLt = {
  sign_up: {
    email_label: 'El. paštas',
    password_label: 'Slaptažodis',
    email_input_placeholder: 'Jūsų el. pašto adresas',
    password_input_placeholder: 'Jūsų slaptažodis',
    button_label: 'Registruotis',
    loading_button_label: 'Registruojama…',
    social_provider_text: 'Prisijungti su {{provider}}',
    link_text: 'Neturite paskyros? Registruokitės',
    confirmation_text:
      'Patikrinkite el. paštą – ten rasite patvirtinimo nuorodą',
  },
  sign_in: {
    email_label: 'El. paštas',
    password_label: 'Slaptažodis',
    email_input_placeholder: 'Jūsų el. pašto adresas',
    password_input_placeholder: 'Jūsų slaptažodis',
    button_label: 'Prisijungti',
    loading_button_label: 'Jungiamasi…',
    social_provider_text: 'Prisijungti su {{provider}}',
    link_text: 'Jau turite paskyrą? Prisijunkite',
  },
  magic_link: {
    email_input_label: 'El. paštas',
    email_input_placeholder: 'Jūsų el. pašto adresas',
    button_label: 'Siųsti prisijungimo nuorodą',
    loading_button_label: 'Siunčiama…',
    link_text: 'Gauti prisijungimo nuorodą el. paštu',
    confirmation_text:
      'Patikrinkite el. paštą – išsiuntėme prisijungimo nuorodą',
  },
  forgotten_password: {
    email_label: 'El. paštas',
    password_label: 'Slaptažodis',
    email_input_placeholder: 'Jūsų el. pašto adresas',
    button_label: 'Siųsti slaptažodžio atkūrimo instrukcijas',
    loading_button_label: 'Siunčiama…',
    link_text: 'Pamiršote slaptažodį?',
    confirmation_text:
      'Patikrinkite el. paštą – išsiuntėme slaptažodžio atkūrimo nuorodą',
  },
  update_password: {
    password_label: 'Naujas slaptažodis',
    password_input_placeholder: 'Įveskite naują slaptažodį',
    button_label: 'Atnaujinti slaptažodį',
    loading_button_label: 'Atnaujinama…',
    confirmation_text: 'Slaptažodis sėkmingai atnaujintas',
  },
  verify_otp: {
    email_input_label: 'El. paštas',
    email_input_placeholder: 'Jūsų el. pašto adresas',
    phone_input_label: 'Telefono numeris',
    phone_input_placeholder: 'Jūsų telefono numeris',
    token_input_label: 'Vienkartinis kodas',
    token_input_placeholder: 'Įveskite kodą',
    button_label: 'Patvirtinti',
    loading_button_label: 'Jungiamasi…',
  },
};

export default function Login() {
  const location = useLocation();

  const shouldHideButton = location.pathname === "/login" && location.search;

  const queryParams = new URLSearchParams(location.search);

  const authViewFromUrl = useMemo(() => {
    const v = queryParams.get('view');
    return AUTH_UI_VIEWS.includes(v) ? v : 'sign_in';
  }, [location.search]);

  const [authView, setAuthView] = useState(authViewFromUrl);

  useEffect(() => {
    if (isPasswordRecoveryActive()) {
      setAuthView('update_password');
      return;
    }
    setAuthView(authViewFromUrl);
  }, [authViewFromUrl, location.hash, location.search]);

  useEffect(() => {
    captureRecoveryFromUrl();
    if (isPasswordRecoveryActive()) {
      setAuthView('update_password');
    }
  }, [location.hash, location.search]);

  const redirectTo = useMemo(() => {
    const origin = window.location.origin;
    if (authView === 'sign_up') {
      return `${origin}/sveiki`;
    }
    if (authView === 'forgotten_password') {
      return `${origin}/auth`;
    }
    if (authView === 'update_password') {
      return `${origin}/`;
    }
    return `${origin}/`;
  }, [authView]);

  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery();
        setAuthView('update_password');
      }
      if (event === 'USER_UPDATED') {
        clearPasswordRecovery();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Accessing the token (for manual API calls to your AWS Lambda)
  const token = session?.access_token;

  return (
    <> 
    <Helmet>
      <title>Ką rengtis pagal Viliją Rušinskaitę - Prisijungti</title>
    </Helmet>
      <Box minH='2vw' />
      <Center px={{ base: 4, sm: 6, md: 8 }} py={{ base: 4, sm: 6 }}>
        <Box
          w="100%"
          maxW="600px"
          mx="auto"
          sx={{
            '& > div': {
              width: '100%',
              maxWidth: '100%',
            },
            '& button, & input, & label': {
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
          }}
        >
          <Auth
            key={authView}
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              extend: true,
              variables: {
                default: {
                  colors: {
                    brand: '#FB8FFF',
                    brandAccent: '#e076e8',
                    brandButtonText: '#1a1a1a',
                  },
                  radii: {
                    borderRadiusButton: '8px',
                    buttonBorderRadius: '8px',
                    inputBorderRadius: '8px',
                  },
                  space: {
                    inputPadding: '12px 14px',
                    buttonPadding: '12px 14px',
                  },
                },
              },
            }}
            localization={{ variables: authUiLt }}
            providers={['google']}
            queryParams={{
              prompt: 'select_account',
            }}
            redirectTo={redirectTo}
            view={authView}
            showLinks={true}
          />
        </Box>
      </Center>
      <Box minH='2vw' />   
    </>
  );
}